import type { Config } from '@puckeditor/core'

import {
  CommunityCTASection,
  GENERAL_V1_COMMUNITY_CTA_GRADIENT,
} from '@/components/homepage/non-fsds-general-v1/community-cta-section'
import CommunityFeedbackSection from '@/components/homepage/non-fsds-general-v1/community-feedback-section'
import { CourseJourneySection } from '@/components/homepage/non-fsds-general-v1/course-journey-section'
import { FaqSection } from '@/components/homepage/non-fsds-general-v1/faq-section'
import GeneralV1HeroSection from '@/components/homepage/non-fsds-general-v1/hero-section'
import IntroStatementSection from '@/components/homepage/non-fsds-general-v1/intro-statement-section'
import { ValueMentorSection } from '@/components/homepage/non-fsds-general-v1/value-mentor-section'
import NonFsdsCorporateCustomerSection from '@/components/homepage/non-fsds-sections/non-fsds-corporate-customer-section'
import { ColorFieldRender, ImageFieldRender } from '@/components/puck/puck-custom-fields'
import { StringListFieldRender } from '@/components/puck/puck-string-list-field'

import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

import type {
  NonFsdsGeneralV1CourseItem,
  NonFsdsGeneralV1FeedbackDialog,
  NonFsdsGeneralV1IntroSegment,
  NonFsdsGeneralV1PartnerItem,
  NonFsdsGeneralV1PartnerSettings,
} from '@/types/non-fsds-general-v1.type'

/** Editable image-backed defaults copied from the shared FSDS CorporateCustomer section. */
export const GENERAL_V1_DEFAULT_CORPORATE_CUSTOMERS: NonFsdsGeneralV1PartnerItem[] = [
  {
    id: 'partner-1',
    name: 'Microsoft',
    logo: '/templates/general-v1/assets/corporate-microsoft.svg',
    alt: 'Microsoft logo',
  },
  {
    id: 'partner-2',
    name: 'Google',
    logo: '/templates/general-v1/assets/corporate-google.svg',
    alt: 'Google logo',
  },
  {
    id: 'partner-3',
    name: 'Amazon',
    logo: '/templates/general-v1/assets/corporate-amazon.svg',
    alt: 'Amazon logo',
  },
  {
    id: 'partner-4',
    name: 'Apple',
    logo: '/templates/general-v1/assets/corporate-apple.svg',
    alt: 'Apple logo',
  },
  {
    id: 'partner-5',
    name: 'Meta',
    logo: '/templates/general-v1/assets/corporate-meta.svg',
    alt: 'Meta logo',
  },
]

export const GENERAL_V1_DEFAULT_COMMUNITY_FEEDBACK: NonFsdsGeneralV1FeedbackDialog = {
  enabled: true,
  eyebrow: 'Learner voices',
  title: 'What Our Community Says!',
  testimonials: [
    {
      id: '1',
      quote: 'This platform changed how I approach learning. The community support is incredible.',
      avatar: '/templates/general-v1/assets/learner-avatar-unsplash-01.webp',
      name: 'Minh Nguyen',
      role: 'Full-Stack Developer',
    },
    {
      id: '2',
      quote: 'The mentors here are world-class. I went from beginner to confident in months.',
      avatar: '/templates/general-v1/assets/learner-avatar-unsplash-02.webp',
      name: 'Sarah Chen',
      role: 'Data Scientist',
    },
    {
      id: '3',
      quote: 'Best investment in my career. The structured learning path kept me on track.',
      avatar: '/templates/general-v1/assets/learner-avatar-unsplash-03.webp',
      name: 'Alex Park',
      role: 'UX Designer',
    },
  ],
  autoPlay: true,
  autoPlayDelayMs: 5000,
  loop: true,
  pauseOnHover: true,
  stopOnInteraction: false,
  showArrows: true,
  desktopColumns: 3,
  backgroundColor: '#021127',
  accentColor: '#0A6CF5',
}

export const GENERAL_V1_DEFAULT_CORPORATE_SETTINGS: Required<NonFsdsGeneralV1PartnerSettings> = {
  enabled: true,
  heading: 'Corporate Customers',
  subheading: '',
  showHeading: true,
  headingColor: '#1a1a2e',
  subheadingColor: 'rgba(26,26,46,0.7)',
  backgroundColor: '#FAFAFA',
  textColor: '#1a1a2e',
  logoSource: 'custom',
  logoSize: 'h-8',
  autoPlay: true,
  durationSeconds: 25,
  pauseOnHover: true,
  movementDirection: 'horizontal',
  reverse: false,
  fade: true,
  numberOfCopies: 5,
  gapPx: 48,
}

/**
 * Component keys for the General V1 template.
 * Used to register components in the Puck editor category.
 */
export const GENERAL_V1_COMPONENT_KEYS = [
  'GeneralV1HeroSection',
  'GeneralV1IntroStatementSection',
  'GeneralV1CourseJourney',
  'GeneralV1CommunityFeedback',
  'GeneralV1ValueMentor',
  'GeneralV1PartnerLogos',
  'GeneralV1FAQ',
  'GeneralV1CommunityCTA',
] as const

interface HiddenSectionPlaceholderProps {
  label: string
  testId: string
}

function HiddenSectionPlaceholder({ label, testId }: HiddenSectionPlaceholderProps) {
  return (
    <section
      data-testid={testId}
      className="mx-auto my-4 flex min-h-24 w-full max-w-[1200px] items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-5 text-center text-sm text-slate-600"
    >
      <span>
        <strong className="font-semibold text-slate-800">{label}</strong> is hidden. Select this block and set{' '}
        <strong className="font-semibold text-slate-800">Show Section</strong> to Yes to display it again.
      </span>
    </section>
  )
}

/**
 * Modular Puck component definitions for General V1.
 *
 * Spread into the main puckConfig.components to register without
 * modifying existing NonFsds* registrations.
 */
export const generalV1PuckComponents: Config['components'] = {
  GeneralV1HeroSection: {
    label: 'General V1: Hero',
    defaultProps: {
      title: 'Learn. Connect. Grow.',
      description:
        'Follow clear learning paths, work with experienced mentors, and turn steady practice into real progress.',
      ctaLabel: 'Explore',
      ctaUrl: TEMPLATE_DEFAULT_URLS.learningPath,
      heroImage: '/templates/general-v1/assets/hero-background.png',
      focalPoint: { x: 0.5, y: 0.5 },
      learnerCount: '2,500+',
      learnerLabel: 'Active Learners',
      learnerAvatars: [
        { image: '/templates/general-v1/assets/learner-avatar-unsplash-01.webp' },
        { image: '/templates/general-v1/assets/learner-avatar-unsplash-02.webp' },
        { image: '/templates/general-v1/assets/learner-avatar-unsplash-03.webp' },
        { image: '/templates/general-v1/assets/learner-avatar-unsplash-04.webp' },
        { image: '/templates/general-v1/assets/learner-avatar-unsplash-05.webp' },
      ],
      isCourseEnrolled: false,
      isEnglish: true,
    },
    fields: {
      title: { label: 'Heading', type: 'text' },
      description: { label: 'Description', type: 'textarea' },
      ctaLabel: { label: 'CTA Label', type: 'text' },
      ctaUrl: { label: 'CTA URL', type: 'text' },
      heroImage: { label: 'Hero Image', type: 'custom', render: ImageFieldRender },
      focalPoint: {
        label: 'Image Focal Point',
        type: 'object',
        objectFields: {
          x: { label: 'Horizontal (0-1)', type: 'number', min: 0, max: 1 },
          y: { label: 'Vertical (0-1)', type: 'number', min: 0, max: 1 },
        },
      },
      learnerCount: { label: 'Learner Count', type: 'text' },
      learnerLabel: { label: 'Learner Label', type: 'text' },
      learnerAvatars: {
        label: 'Learner Avatars',
        type: 'array',
        arrayFields: {
          image: { label: 'Image', type: 'custom', render: ImageFieldRender },
          alt: { label: 'Alternative Text', type: 'text' },
        },
        defaultItemProps: {
          image: '',
          alt: 'Learner avatar',
        },
        getItemSummary: (item: Record<string, unknown>, index) =>
          (item.alt as string) || `Learner ${Number(index ?? 0) + 1}`,
      },
      isCourseEnrolled: {
        label: 'User Enrolled',
        type: 'radio',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      isEnglish: {
        label: 'English',
        type: 'radio',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
    },
    render: ({
      title,
      description,
      ctaLabel,
      ctaUrl,
      heroImage,
      focalPoint,
      learnerCount,
      learnerLabel,
      learnerAvatars,
      feedbackDialog,
      isCourseEnrolled,
      isEnglish,
      puck: _puck,
    }) => (
      <GeneralV1HeroSection
        title={title}
        description={description}
        ctaLabel={ctaLabel}
        ctaUrl={ctaUrl}
        heroImage={heroImage}
        focalPoint={focalPoint}
        learnerCount={learnerCount}
        learnerLabel={learnerLabel}
        learnerAvatars={learnerAvatars}
        isCourseEnrolled={isCourseEnrolled}
        isEnglish={isEnglish}
        feedbackDialog={feedbackDialog}
      />
    ),
  },
  GeneralV1IntroStatementSection: {
    label: 'General V1: Intro Statement',
    defaultProps: {
      segments: [
        { text: 'A focused learning community helps every learner build lasting momentum. ', highlighted: true },
        {
          text: 'Clear paths, experienced mentors, practical projects, and an encouraging network turn steady effort into progress you can see, measure, and build on as you move from foundational skills to confident real-world practice.',
          highlighted: false,
        },
      ],
      scrollFill: true,
    },
    fields: {
      segments: {
        label: 'Segments',
        type: 'array',
        arrayFields: {
          text: { label: 'Text', type: 'text' },
          highlighted: {
            label: 'Highlighted',
            type: 'radio',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
        },
        defaultItemProps: {
          text: 'New segment',
          highlighted: false,
        },
        getItemSummary: (item: Record<string, unknown>) => (item.text as string) || 'Untitled segment',
      },
      scrollFill: {
        label: 'Scroll Fill',
        type: 'radio',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
    },
    render: ({ segments, scrollFill, puck: _puck }) => (
      <IntroStatementSection segments={(segments || []) as NonFsdsGeneralV1IntroSegment[]} scrollFill={scrollFill} />
    ),
  },
  GeneralV1CourseJourney: {
    label: 'General V1: Course Journey',
    defaultProps: {
      title: 'Your Learning Journey',
      description: 'Follow a structured path from beginner to advanced.',
      courses: [
        {
          id: '1',
          keywords: ['Core', 'Foundation', 'Essential'],
          name: 'Foundation',
          courseCode: 'FND101',
          timeline: '4 weeks',
          level: 'Beginner',
          highlights: ['Core concepts and principles', 'Hands-on starter project', 'Community onboarding'],
          detailTitle: 'Build Your Foundation',
          detailDescription:
            'Develop the essential knowledge, habits, and practical skills needed to begin your learning journey with confidence.',
          highlightsTitle: 'What you will achieve',
          contactLabel: 'Talk to a learning advisor',
          detailLabel: 'Explore Foundation',
          detailUrl: '/learning-path',
          contactUrl: '/contact',
        },
        {
          id: '2',
          keywords: ['Practice', 'Hands-on', 'Projects'],
          name: 'Practice',
          courseCode: 'PRC201',
          timeline: '6 weeks',
          level: 'Intermediate',
          highlights: ['Real-world project exercises', 'Peer code reviews', 'Portfolio building'],
          detailTitle: 'Turn Knowledge into Practice',
          detailDescription:
            'Apply what you know through guided projects, constructive peer feedback, and portfolio-ready outcomes.',
          highlightsTitle: 'Skills you will build',
          contactLabel: 'Discuss your project path',
          detailLabel: 'Explore Practice',
          detailUrl: '/learning-path',
          contactUrl: '/contact',
        },
        {
          id: '3',
          keywords: ['Advanced', 'Deep Dive', 'Expert'],
          name: 'Advanced',
          courseCode: 'ADV301',
          timeline: '8 weeks',
          level: 'Advanced',
          highlights: ['Deep dive into specializations', 'Architecture patterns', 'System design'],
          detailTitle: 'Deepen Your Expertise',
          detailDescription:
            'Strengthen your technical judgment by exploring advanced specializations, architecture, and scalable system design.',
          highlightsTitle: 'Advanced outcomes',
          contactLabel: 'Plan your specialization',
          detailLabel: 'Explore Advanced',
          detailUrl: '/learning-path',
          contactUrl: '/contact',
        },
        {
          id: '4',
          keywords: ['Mentorship', 'Career', 'Growth'],
          name: 'Career Accelerator',
          courseCode: 'CAR401',
          timeline: '10 weeks',
          level: 'Professional',
          highlights: ['1-on-1 mentor sessions', 'Interview preparation', 'Industry connections'],
          detailTitle: 'Accelerate Your Career',
          detailDescription:
            'Prepare for your next professional opportunity with mentor guidance, interview practice, and industry connections.',
          highlightsTitle: 'Career outcomes',
          contactLabel: 'Talk to a career advisor',
          detailLabel: 'Explore Career Accelerator',
          detailUrl: '/learning-path',
          contactUrl: '/contact',
        },
        {
          id: '5',
          keywords: ['Leadership', 'Community', 'Impact'],
          name: 'Community Leader',
          courseCode: 'LDR501',
          timeline: '12 weeks',
          level: 'Expert',
          highlights: ['Lead study groups', 'Create learning content', 'Mentor new learners'],
          detailTitle: 'Lead and Grow the Community',
          detailDescription:
            'Turn your experience into impact by leading study groups, sharing knowledge, and mentoring the next generation of learners.',
          highlightsTitle: 'Leadership outcomes',
          contactLabel: 'Talk to a community advisor',
          detailLabel: 'Explore Community Leadership',
          detailUrl: '/learning-path',
          contactUrl: '/contact',
        },
      ],
    },
    fields: {
      title: { label: 'Heading', type: 'text' },
      description: { label: 'Description', type: 'textarea' },
      courses: {
        label: 'Courses',
        type: 'array',
        arrayFields: {
          keywords: { label: 'Keywords', type: 'custom', render: StringListFieldRender },
          name: { label: 'Name', type: 'text' },
          courseCode: { label: 'Course Code', type: 'text' },
          timeline: { label: 'Timeline', type: 'text' },
          level: { label: 'Level', type: 'text' },
          highlights: { label: 'Highlights', type: 'custom', render: StringListFieldRender },
          detailTitle: { label: 'Popup Title', type: 'text' },
          detailDescription: { label: 'Popup Description', type: 'textarea' },
          highlightsTitle: { label: 'Popup Highlights Title', type: 'text' },
          contactLabel: { label: 'Popup Contact Label', type: 'text' },
          detailLabel: { label: 'Popup Course Label', type: 'text' },
          detailUrl: { label: 'Detail URL', type: 'text' },
          contactUrl: { label: 'Contact URL', type: 'text' },
        },
        defaultItemProps: {
          id: 'new',
          keywords: [],
          name: 'New Course',
          courseCode: '',
          timeline: '4 weeks',
          level: 'Beginner',
          highlights: [
            'Understand the core concepts',
            'Complete a guided practical project',
            'Apply your skills with community support',
          ],
          detailTitle: 'Explore This Course',
          detailDescription:
            'Discover the course structure, practical learning outcomes, and support available throughout your journey.',
          highlightsTitle: 'What you will achieve',
          contactLabel: 'Talk to a learning advisor',
          detailLabel: 'Explore Course Details',
          detailUrl: '/learning-path',
          contactUrl: '/contact',
        },
        getItemSummary: (item: Record<string, unknown>) => (item.name as string) || 'Untitled course',
      },
    },
    render: ({ title, description, courses, puck: _puck }) => (
      <CourseJourneySection
        title={title}
        description={description}
        courses={(courses || []) as NonFsdsGeneralV1CourseItem[]}
      />
    ),
  },
  GeneralV1CommunityFeedback: {
    label: 'General V1: What Our Community Says',
    defaultProps: GENERAL_V1_DEFAULT_COMMUNITY_FEEDBACK,
    fields: {
      enabled: {
        label: 'Show Section',
        type: 'radio',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      eyebrow: { label: 'Eyebrow', type: 'text' },
      title: { label: 'Heading', type: 'text' },
      testimonials: {
        label: 'Learner Testimonials',
        type: 'array',
        arrayFields: {
          id: { label: 'ID', type: 'text' },
          quote: { label: 'Quote', type: 'textarea' },
          avatar: { label: 'Avatar', type: 'custom', render: ImageFieldRender },
          name: { label: 'Name', type: 'text' },
          role: { label: 'Role', type: 'text' },
        },
        defaultItemProps: {
          id: 'new-testimonial',
          quote: 'Share a specific learning outcome or community experience.',
          avatar: '',
          name: 'Learner name',
          role: 'Community member',
        },
        getItemSummary: (item: Record<string, unknown>) => (item.name as string) || 'Unnamed testimonial',
      },
      autoPlay: {
        label: 'Auto Play',
        type: 'radio',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      autoPlayDelayMs: { label: 'Auto Play Delay (ms)', type: 'number', min: 1000, max: 30000 },
      loop: {
        label: 'Infinite Loop',
        type: 'radio',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      pauseOnHover: {
        label: 'Pause on Hover',
        type: 'radio',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      stopOnInteraction: {
        label: 'Stop After Manual Navigation',
        type: 'radio',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      showArrows: {
        label: 'Show Navigation Arrows',
        type: 'radio',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      desktopColumns: {
        label: 'Cards on Desktop',
        type: 'select',
        options: [
          { label: '1 card', value: 1 },
          { label: '2 cards', value: 2 },
          { label: '3 cards', value: 3 },
        ],
      },
      backgroundColor: { label: 'Background Color', type: 'custom', render: ColorFieldRender },
      accentColor: { label: 'Accent Color', type: 'custom', render: ColorFieldRender },
    },
    render: ({ puck, ...props }) => {
      const feedbackProps = props as unknown as NonFsdsGeneralV1FeedbackDialog

      if (!feedbackProps.enabled && puck.isEditing) {
        return <HiddenSectionPlaceholder label="What Our Community Says" testId="gv1-hidden-community-feedback" />
      }

      return <CommunityFeedbackSection {...feedbackProps} />
    },
  },
  GeneralV1PartnerLogos: {
    label: 'General V1: Corporate Customers',
    defaultProps: {
      partners: GENERAL_V1_DEFAULT_CORPORATE_CUSTOMERS,
      settings: GENERAL_V1_DEFAULT_CORPORATE_SETTINGS,
    },
    fields: {
      partners: {
        label: 'Corporate Customers',
        type: 'array',
        arrayFields: {
          id: { label: 'ID', type: 'text' },
          name: { label: 'Name', type: 'text' },
          logo: { label: 'Logo', type: 'custom', render: ImageFieldRender },
          alt: { label: 'Alternative Text', type: 'text' },
          url: { label: 'Link URL', type: 'text' },
          title: { label: 'Link Title', type: 'text' },
        },
        defaultItemProps: {
          id: 'new',
          name: 'Corporate Customer',
          logo: '',
          alt: '',
          url: '',
          title: '',
        },
        getItemSummary: (item: Record<string, unknown>) => (item.name as string) || 'Unnamed customer',
      },
      settings: {
        label: 'Display & Auto Play Controls',
        type: 'object',
        objectFields: {
          enabled: {
            label: 'Show Section',
            type: 'radio',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
          heading: { label: 'Heading', type: 'text' },
          subheading: { label: 'Subheading', type: 'textarea' },
          showHeading: {
            label: 'Show Heading',
            type: 'radio',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
          headingColor: { label: 'Heading Color', type: 'custom', render: ColorFieldRender },
          subheadingColor: { label: 'Subheading Color', type: 'custom', render: ColorFieldRender },
          backgroundColor: { label: 'Background Color', type: 'custom', render: ColorFieldRender },
          textColor: { label: 'Text Color', type: 'custom', render: ColorFieldRender },
          logoSource: {
            label: 'Logo Source',
            type: 'select',
            options: [
              { label: 'Editable logos below', value: 'custom' },
              { label: 'Built-in FSDS logos', value: 'default' },
            ],
          },
          logoSize: {
            label: 'Logo Size',
            type: 'select',
            options: [
              { label: 'Small (24px)', value: 'h-6' },
              { label: 'Medium (32px)', value: 'h-8' },
              { label: 'Large (40px)', value: 'h-10' },
              { label: 'Extra Large (48px)', value: 'h-12' },
              { label: 'Super Large (64px)', value: 'h-16' },
            ],
          },
          autoPlay: {
            label: 'Auto Play',
            type: 'radio',
            options: [
              { label: 'On', value: true },
              { label: 'Off', value: false },
            ],
          },
          durationSeconds: { label: 'Cycle Duration (seconds)', type: 'number', min: 5, max: 120 },
          pauseOnHover: {
            label: 'Pause on Hover',
            type: 'radio',
            options: [
              { label: 'On', value: true },
              { label: 'Off', value: false },
            ],
          },
          movementDirection: {
            label: 'Movement Direction',
            type: 'select',
            options: [
              { label: 'Horizontal', value: 'horizontal' },
              { label: 'Vertical', value: 'vertical' },
            ],
          },
          reverse: {
            label: 'Reverse Direction',
            type: 'radio',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
          fade: {
            label: 'Fade Edges',
            type: 'radio',
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
          numberOfCopies: { label: 'Loop Copies', type: 'number', min: 1, max: 10 },
          gapPx: { label: 'Logo Gap (px)', type: 'number', min: 0, max: 120 },
        },
      },
    },
    render: ({ partners, settings, puck }) => {
      const resolvedSettings = {
        ...GENERAL_V1_DEFAULT_CORPORATE_SETTINGS,
        ...((settings || {}) as NonFsdsGeneralV1PartnerSettings),
      }
      if (!resolvedSettings.enabled) {
        return puck.isEditing ? (
          <HiddenSectionPlaceholder label="Corporate Customers" testId="gv1-hidden-corporate-customers" />
        ) : (
          <></>
        )
      }

      const configuredPartners =
        ((partners || []) as NonFsdsGeneralV1PartnerItem[]).length > 0
          ? ((partners || []) as NonFsdsGeneralV1PartnerItem[])
          : GENERAL_V1_DEFAULT_CORPORATE_CUSTOMERS
      const corporateLogos = configuredPartners.map((partner, index) => {
        const fallback = GENERAL_V1_DEFAULT_CORPORATE_CUSTOMERS[index % GENERAL_V1_DEFAULT_CORPORATE_CUSTOMERS.length]

        return {
          title: partner.name || fallback.name,
          image: partner.logo || fallback.logo,
          alt: partner.alt || fallback.alt,
          href: partner.url,
          linkTitle: partner.title,
        }
      })

      return (
        <NonFsdsCorporateCustomerSection
          heading={resolvedSettings.heading}
          subheading={resolvedSettings.subheading}
          showHeading={resolvedSettings.showHeading}
          headingColor={resolvedSettings.headingColor}
          subheadingColor={resolvedSettings.subheadingColor}
          bgColor={resolvedSettings.backgroundColor}
          textColor={resolvedSettings.textColor}
          logos={corporateLogos}
          logoSource={resolvedSettings.logoSource}
          logoSize={resolvedSettings.logoSize}
          autoPlay={resolvedSettings.autoPlay}
          durationSeconds={resolvedSettings.durationSeconds}
          pauseOnHover={resolvedSettings.pauseOnHover}
          movementDirection={resolvedSettings.movementDirection}
          reverse={resolvedSettings.reverse}
          fade={resolvedSettings.fade}
          numberOfCopies={resolvedSettings.numberOfCopies}
          gapPx={resolvedSettings.gapPx}
        />
      )
    },
  },
  GeneralV1ValueMentor: {
    label: 'General V1: Value & Mentors',
    defaultProps: {
      title: 'Learn from Industry Experts',
      description: 'Our mentors bring real-world experience to guide your growth.',
      stats: [
        { icon: '/templates/general-v1/assets/user-square.svg', value: '21+', label: 'Mentors' },
        { icon: '/templates/general-v1/assets/book-open.webp', value: '61+', label: 'Sessions' },
        { icon: '/templates/general-v1/assets/users-group.svg', value: '194+', label: 'Community' },
        { icon: '/templates/general-v1/assets/check-all.svg', value: '85%', label: 'Completion Rate' },
      ],
      mentors: [
        {
          id: 'mentor-1',
          name: 'William Tony',
          role: 'Senior Engineering Lead',
          description: 'Builds practical learning programs around production engineering experience.',
          image: '/templates/general-v1/assets/learner-avatar-unsplash-02.webp',
        },
        {
          id: 'mentor-2',
          name: 'Henry Dozta',
          role: 'Data Science Mentor',
          description: 'Guides learners through applied data projects and structured technical practice.',
          image: '/templates/general-v1/assets/learner-avatar-unsplash-04.webp',
        },
        {
          id: 'mentor-3',
          name: 'David Nguyen',
          role: 'Machine Learning Instructor',
          description: 'Focuses on clear foundations, real-world ML workflows, and continuous feedback.',
          image: '/templates/general-v1/assets/learner-avatar-unsplash-05.webp',
        },
      ],
      experienceDescription:
        'Join a vibrant community of learners and mentors who are passionate about technology and continuous growth.',
      experienceKeywords: [
        { value: 'Community Learning' },
        { value: 'Hands-on Projects' },
        { value: 'Career Growth' },
        { value: 'Expert Mentorship' },
        { value: 'Peer Networking' },
      ],
    },
    fields: {
      title: { label: 'Heading', type: 'text' },
      description: { label: 'Description', type: 'textarea' },
      experienceDescription: { label: 'Experience Description', type: 'textarea' },
      stats: {
        label: 'Statistics',
        type: 'array',
        arrayFields: {
          icon: { label: 'Icon', type: 'custom', render: ImageFieldRender },
          value: { label: 'Value', type: 'text' },
          label: { label: 'Label', type: 'text' },
        },
        defaultItemProps: {
          icon: '',
          value: '0',
          label: 'New Metric',
        },
        getItemSummary: (item: Record<string, unknown>) => `${item.label}: ${item.value}`,
      },
      mentors: {
        label: 'Mentors',
        type: 'array',
        arrayFields: {
          name: { label: 'Name', type: 'text' },
          role: { label: 'Role', type: 'text' },
          description: { label: 'Description', type: 'textarea' },
          image: { label: 'Image', type: 'custom', render: ImageFieldRender },
        },
        defaultItemProps: {
          id: 'new',
          name: 'New Mentor',
          role: 'Instructor',
          description: 'Mentor bio',
          image: '',
        },
        getItemSummary: (item: Record<string, unknown>) => (item.name as string) || 'Unnamed mentor',
      },
      experienceKeywords: {
        label: 'Experience Keywords',
        type: 'array',
        arrayFields: {
          value: { label: 'Keyword', type: 'text' },
        },
        defaultItemProps: {
          value: 'New Keyword',
        },
        getItemSummary: (item: Record<string, unknown>) => (item.value as string) || 'Untitled',
      },
    },
    render: ({ title, description, stats, mentors, experienceDescription, experienceKeywords, puck: _puck }) => (
      <ValueMentorSection
        title={title}
        description={description}
        stats={stats || []}
        mentors={mentors || []}
        experienceDescription={experienceDescription}
        experienceKeywords={
          experienceKeywords
            ? (experienceKeywords as Array<string | { value: string }>).map(keyword =>
                typeof keyword === 'string' ? keyword : keyword.value
              )
            : []
        }
        isEnglish={true}
      />
    ),
  },
  GeneralV1FAQ: {
    label: 'General V1: FAQ',
    defaultProps: {
      title: 'Frequently Asked Questions',
      description: 'Find answers to common questions about our platform.',
      contactLabel: 'Contact Support',
      contactUrl: '/contact',
      faqs: [
        {
          id: 'faq-1',
          q: 'How do I get started?',
          a: 'Sign up for free and explore available courses. Our onboarding guide will help you choose the right learning path for your goals.',
        },
        {
          id: 'faq-2',
          q: 'Is there a free trial?',
          a: 'Yes, you can explore the platform with no commitment. Access starter courses and community features at no cost.',
        },
        {
          id: 'faq-3',
          q: 'How are mentors selected?',
          a: 'Our mentors go through a rigorous selection process. They bring verified industry experience and a passion for teaching.',
        },
        {
          id: 'faq-4',
          q: 'Can I switch learning paths?',
          a: 'Absolutely. You can change your learning path at any time. Your progress is saved and you can always come back.',
        },
      ],
      defaultOpenId: 'faq-3',
    },
    fields: {
      title: { label: 'Heading', type: 'text' },
      description: { label: 'Description', type: 'textarea' },
      contactLabel: { label: 'Contact Label', type: 'text' },
      contactUrl: { label: 'Contact URL', type: 'text' },
      defaultOpenId: { label: 'Default Open FAQ ID', type: 'text' },
      faqs: {
        label: 'Questions',
        type: 'array',
        arrayFields: {
          id: { label: 'ID', type: 'text' },
          q: { label: 'Question', type: 'text' },
          a: { label: 'Answer', type: 'textarea' },
        },
        defaultItemProps: {
          id: 'new',
          q: 'New question?',
          a: 'Answer here.',
        },
        getItemSummary: (item: Record<string, unknown>) => (item.q as string) || 'Untitled question',
      },
    },
    render: ({ title, description, contactLabel, contactUrl, defaultOpenId, faqs, puck: _puck }) => (
      <FaqSection
        title={title}
        description={description}
        contactLabel={contactLabel}
        contactUrl={contactUrl}
        defaultOpenId={defaultOpenId}
        faqs={faqs}
      />
    ),
  },
  GeneralV1CommunityCTA: {
    label: 'General V1: Community CTA',
    defaultProps: {
      title: 'Ready to start your journey?',
      description: 'Join thousands of learners building their future today.',
      ctaLabel: 'Get Started Free',
      ctaUrl: TEMPLATE_DEFAULT_URLS.signUp,
      secondaryLabel: '',
      secondaryUrl: '',
      gradientFrom: GENERAL_V1_COMMUNITY_CTA_GRADIENT.from,
      gradientTo: GENERAL_V1_COMMUNITY_CTA_GRADIENT.to,
    },
    fields: {
      title: { label: 'Heading', type: 'text' },
      description: { label: 'Description', type: 'textarea' },
      ctaLabel: { label: 'CTA Label', type: 'text' },
      ctaUrl: { label: 'CTA URL', type: 'text' },
      secondaryLabel: { label: 'Secondary CTA Label', type: 'text' },
      secondaryUrl: { label: 'Secondary CTA URL', type: 'text' },
      gradientFrom: { label: 'Gradient Start', type: 'custom', render: ColorFieldRender },
      gradientTo: { label: 'Gradient End', type: 'custom', render: ColorFieldRender },
    },
    render: ({
      title,
      description,
      ctaLabel,
      ctaUrl,
      secondaryLabel,
      secondaryUrl,
      gradientFrom,
      gradientTo,
      puck: _puck,
    }) => (
      <CommunityCTASection
        title={title}
        description={description}
        ctaLabel={ctaLabel}
        ctaUrl={ctaUrl}
        secondaryLabel={secondaryLabel}
        secondaryUrl={secondaryUrl}
        gradientFrom={gradientFrom}
        gradientTo={gradientTo}
      />
    ),
  },
}
